import React, {useContext, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import uniqueId from 'lodash.uniqueid';
import {parseErrorMessages} from 'src/contexts/message-context/message-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';

import BoxLoading from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label, {Tags} from '@pb/kit-component-label';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';


import {TabActionsBar} from '../../../../components/tab-actions-bar';
import {TabContent} from '../../../../components/tab-content';
import {getCredits} from '../../../../utils';
import {crmIntegrationSelector} from '../../setup/services/selectors';
import {ContractsList} from '../components/contracts-list';
import {usePricingActions} from '../hooks';
import {
    allContractNumbersSelector,
    contractsDataSelector,
    getPricingBusy,
    getPricingError,
} from '../services/selectors';

import {AddContractDialog} from './add-contract-dialog';

import type {ParseContracts} from '../../contracts/parse-contracts';


export const PricingView = () => {
    const {locale} = useContext(LocaleContext);
    const params = useParams<{accountId: string}>();
    const accountId = params.accountId ? +params.accountId : -1;
    const {canEditApiV2} = useAdminAccessRight();
    const canEdit = canEditApiV2(accountId);

    const [lastCreatedNumber, setLastCreatedNumber] = useState<string | null>(null);
    const pricingBusy = useSelector(getPricingBusy);
    const error = useSelector(getPricingError);
    const allContractNumbers: Array<string> = useSelector(allContractNumbersSelector);
    const contractsData: ParseContracts | null = useSelector(contractsDataSelector);
    const crmIntegration: boolean = useSelector(crmIntegrationSelector);
    const {onDownload, onCloseError, onUpdateCredits, onUpdateContractInfo} = usePricingActions();
    const {onCreateNote, onCloseNote} = usePushNotification();
    const errorNoteIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!error) {
            if (errorNoteIdRef.current) {
                onCloseNote(errorNoteIdRef.current);
                errorNoteIdRef.current = null;
            }
            return;
        }

        if (errorNoteIdRef.current) {
            onCloseNote(errorNoteIdRef.current);
        }

        const noteId = uniqueId('admin-account-api-pricing-error-');
        errorNoteIdRef.current = noteId;
        onCreateNote({
            id: noteId,
            status: Status.ERROR,
            buttonProps: {
                iconProps: {type: Icons.CLOSE},
                onClick: () => {
                    onCloseError();
                },
            },
            body: () => parseErrorMessages(error.message || ''),
        });

        return () => {
            if (errorNoteIdRef.current === noteId) {
                onCloseNote(noteId);
                errorNoteIdRef.current = null;
            }
        };
    }, [error, onCloseError, onCreateNote, onCloseNote]);

    if (!contractsData) {
        return null;
    }

    const {activeContracts, pastContracts, remainingActiveCredits} = contractsData;

    const isActive = activeContracts?.length > 0;
    const isPast = pastContracts?.length > 0;
    const hasUsage = activeContracts.some(contract => (
        contract.creditBalanceInfo.creditChange ||
        contract.creditBalanceInfo.creditUsed ||
        contract.creditBalanceInfo.overageUsed
    )) || pastContracts.some(contract => (
        contract.creditBalanceInfo.creditChange ||
        contract.creditBalanceInfo.creditUsed ||
        contract.creditBalanceInfo.overageUsed
    ));

    return (
        <>
            <BoxLoading loading={pricingBusy}>
                {!crmIntegration && (
                    <TabActionsBar>
                        <Group style={{height: '100%'}} justify="end" alignItems="stretch">
                            <FlatButton
                                startIcon={<Icon type={Icons.DOWNLOAD}/>}
                                disabled={!isActive && !isPast || !hasUsage}
                                onClick={onDownload}>
                                <b>Download</b>
                            </FlatButton>
                        </Group>
                    </TabActionsBar>
                )}
                <TabContent increasePadding={true}>
                    <Group direction="v" gap="xls" alignItems="start">
                        <Group direction="v" gap="s">
                            <Item className="pb-xs">
                                <Typography fontWeight="semi-bold" variant="heading-level-3">Active Contracts</Typography>
                            </Item>

                            {isActive ? (
                                <ContractsList
                                    {...{
                                        lastCreatedNumber,
                                        allContractNumbers,
                                        data: activeContracts,
                                        onUpdateContractInfo,
                                        onUpdateCredits,
                                        disabled: !canEdit,
                                    }}
                                />
                            ) : null}

                            <Group>
                                <Item grow={1}>
                                    <AddContractDialog {...{onCreateContract: setLastCreatedNumber, canEdit}}/>
                                </Item>
                                {isActive && !crmIntegration ? (
                                    <Group gap="xl">
                                        <Label slug={Tags.DIV}>Total Remaining Credits:</Label>
                                        <Item>
                                            {getCredits(remainingActiveCredits, locale)}
                                        </Item>
                                    </Group>
                                ) : null}
                            </Group>
                        </Group>

                        {isPast ? (
                            <Group direction="v" gap="s" alignItems="start">
                                <Item className="pb-xs">
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">Past Contracts</Typography>
                                </Item>

                                <ContractsList
                                    {...{
                                        lastCreatedNumber,
                                        allContractNumbers,
                                        data: pastContracts,
                                        isPastContract: true,
                                        onUpdateContractInfo,
                                        onUpdateCredits,
                                        disabled: !canEdit,
                                    }}
                                />
                            </Group>
                        ) : null}
                    </Group>
                </TabContent>
            </BoxLoading>
        </>
    );
};
